import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';

// ─── Translations ──────────────────────────────────────────────────────────────

const COPY = {
  en: {
    title: 'Accessibility',
    description: 'Adjust the display to suit your needs. Your choices are saved on this device.',
    theme: 'Colour theme',
    light: 'Light',
    dark: 'Dark',
    textSize: 'Text size',
    sizeNormal: 'Normal',
    sizeLarge: 'Large',
    sizeLarger: 'Larger',
    contrast: 'Higher contrast',
    contrastDesc: 'Strengthen text contrast for easier reading',
    underline: 'Underline links',
    underlineDesc: 'Always underline links within content',
    motion: 'Reduce motion',
    motionDesc: 'Minimise animations and transitions',
    reset: 'Reset to defaults',
    statement: 'Accessibility statement',
    close: 'Close',
    on: 'on',
    off: 'off',
    reseted: 'Accessibility settings reset to defaults',
  },
  cy: {
    title: 'Hygyrchedd',
    description: "Addaswch yr arddangosfa i weddu i'ch anghenion. Caiff eich dewisiadau eu cadw ar y ddyfais hon.",
    theme: 'Thema lliw',
    light: 'Golau',
    dark: 'Tywyll',
    textSize: 'Maint y testun',
    sizeNormal: 'Arferol',
    sizeLarge: 'Mawr',
    sizeLarger: 'Mwy',
    contrast: 'Cyferbyniad uwch',
    contrastDesc: "Cryfhau cyferbyniad y testun i'w ddarllen yn haws",
    underline: 'Tanlinellu dolenni',
    underlineDesc: 'Tanlinellu dolenni o fewn cynnwys bob amser',
    motion: 'Lleihau symud',
    motionDesc: 'Lleihau animeiddiadau a phontiadau',
    reset: "Ailosod i'r rhagosodiadau",
    statement: 'Datganiad hygyrchedd',
    close: 'Cau',
    on: 'ymlaen',
    off: 'i ffwrdd',
    reseted: "Ailosodwyd y gosodiadau hygyrchedd i'r rhagosodiadau",
  },
  gd: {
    title: 'So-ruigsinneachd',
    description: 'Atharraich an taisbeanadh a rèir do fheumalachdan. Thèid na roghainnean agad a shàbhaladh air an inneal seo.',
    theme: 'Tèama dhathan',
    light: 'Soilleir',
    dark: 'Dorcha',
    textSize: 'Meud an teacsa',
    sizeNormal: 'Àbhaisteach',
    sizeLarge: 'Mòr',
    sizeLarger: 'Nas motha',
    contrast: 'Iomsgaradh nas àirde',
    contrastDesc: 'Neartaich iomsgaradh an teacsa airson leughadh nas fhasa',
    underline: 'Loidhne fo cheanglaichean',
    underlineDesc: "Cuir loidhne fo cheanglaichean san t-susbaint an-còmhnaidh",
    motion: 'Lùghdaich gluasad',
    motionDesc: 'Lùghdaich beòthachaidhean is gluasadan',
    reset: 'Ath-shuidhich',
    statement: 'Aithris so-ruigsinneachd',
    close: 'Dùin',
    on: 'air',
    off: 'dheth',
    reseted: 'Chaidh na roghainnean so-ruigsinneachd ath-shuidheachadh',
  },
};

const FOCUSABLE =
  'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), [role="switch"], [tabindex]:not([tabindex="-1"])';

// ─── Small accessible primitives ───────────────────────────────────────────────

// A radio group (role radiogroup / radio, aria-checked). The arrow keys move
// and select within the group, as users expect of radio buttons — Left / Up
// the previous option, Right / Down the next, Home / End the first / last —
// without scrolling the page. The radio role is what makes NVDA, JAWS and
// VoiceOver pass the arrow keys to the page instead of using them to read.
// Every option is also its own Tab stop (no roving tabindex), so Tab reaches
// each one, and Enter or Space selects the focused option (WCAG 2.1.1
// Keyboard, 4.1.2 Name, Role, Value).
function Segmented({ label, name, value, options, onChange }) {
  const groupRef = useRef(null);
  const selectedIdx = Math.max(0, options.findIndex((o) => o.value === value));

  const onKeyDown = (e) => {
    const buttons = Array.from(groupRef.current?.querySelectorAll('button') || []);
    const current = buttons.indexOf(document.activeElement);
    const from = current < 0 ? selectedIdx : current;
    let next;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (from + 1) % options.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (from - 1 + options.length) % options.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = options.length - 1;
    else return;
    e.preventDefault();
    onChange(options[next].value);
    buttons[next]?.focus();
  };

  return (
    <div>
      <span id={`${name}-label`} className="block text-sm font-medium text-gray-900 dark:text-dark-100 mb-1.5">
        {label}
      </span>
      <div
        ref={groupRef}
        role="radiogroup"
        aria-labelledby={`${name}-label`}
        onKeyDown={onKeyDown}
        className="grid grid-flow-col auto-cols-fr gap-1 p-1 rounded-xl bg-gray-100 dark:bg-dark-700"
      >
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(opt.value)}
              // The selected segment is a solid fill (blue-700 / blue-300), ≥ 3:1
              // against the group track in both themes, so the state is not
              // conveyed by a faint white-on-grey tint (WCAG 1.4.11). A tick is
              // added so it is not signalled by colour alone (1.4.1).
              className={`inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-700 dark:focus-visible:ring-blue-300 focus-visible:ring-offset-white dark:focus-visible:ring-offset-dark-800 ${
                active
                  ? 'a11y-seg-on bg-blue-700 text-white dark:bg-blue-300 dark:text-dark-950 shadow-sm'
                  : 'text-gray-600 dark:text-dark-300 hover:text-gray-900 dark:hover:text-dark-100 hover:bg-white dark:hover:bg-dark-800'
              }`}
            >
              {active && (
                <svg aria-hidden="true" focusable="false" className="w-3.5 h-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" />
                </svg>
              )}
              <span style={opt.style}>{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// The switch is named by its label alone and the longer text is its
// description, so a screen reader says "Higher contrast, switch, off" and then
// the description, instead of running the two together (WCAG 4.1.2).
// Space / Enter toggle it (native button, ARIA switch pattern). Right turns it
// on and Left turns it off, the way the knob moves, like the radio rows above
// it in the same panel (OA bug #12 / RRO bug #6). Each switch stays its own Tab
// stop: the arrows never move focus to another switch.
function Switch({ label, description, checked, onChange }) {
  const id = useId();
  const onKeyDown = (e) => {
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const on = e.key === 'ArrowRight';
    if (on !== checked) onChange(on);
  };
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={`${id}-label`}
      aria-describedby={description ? `${id}-desc` : undefined}
      onClick={() => onChange(!checked)}
      onKeyDown={onKeyDown}
      className="flex items-start justify-between w-full gap-3 text-left rounded-xl px-1 py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary"
    >
      <span className="min-w-0">
        <span id={`${id}-label`} className="block text-sm font-medium text-gray-900 dark:text-dark-100">{label}</span>
        {description && (
          <span id={`${id}-desc`} className="block text-xs text-gray-500 dark:text-dark-400 mt-0.5">{description}</span>
        )}
      </span>
      <span
        aria-hidden="true"
        className={`relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-accent-primary' : 'bg-gray-300 dark:bg-dark-600'
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </span>
    </button>
  );
}

// ─── Panel ──────────────────────────────────────────────────────────────────────

export default function AccessibilityPanel({
  id,
  open,
  onClose,
  returnFocusRef,
  theme,
  onSetTheme,
  prefs,
  onUpdate,
  onReset,
}) {
  const lang = useLanguage();
  const t = COPY[lang.code] || COPY.en;
  const panelRef = useRef(null);
  const [announcement, setAnnouncement] = useState('');

  const announce = (msg) => setAnnouncement(`${msg} ​`); // zero-width keeps repeats audible

  // Move focus into the panel when it opens; restore it to the trigger on close.
  useEffect(() => {
    if (!open) return undefined;
    const node = panelRef.current;
    const first = node?.querySelector('[data-autofocus]') || node?.querySelector(FOCUSABLE);
    first?.focus();
    // The trigger element is fixed for the lifetime of an open panel.
    const trigger = returnFocusRef?.current;
    return () => trigger?.focus?.();
  }, [open, returnFocusRef]);

  // Esc to close + Tab focus trap (WCAG 2.1.2 No Keyboard Trap / 2.4.3 Focus Order).
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const node = panelRef.current;
      if (!node) return;
      const items = Array.from(node.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (items.length === 0) return;
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  // Keep the focused control fully visible inside the (scrollable) panel —
  // at 200–400% zoom the panel is taller than the viewport (WCAG 1.4.10 /
  // 2.4.11 Focus Not Obscured).
  useEffect(() => {
    if (!open) return undefined;
    const node = panelRef.current;
    const onFocusIn = (e) => e.target?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    node?.addEventListener('focusin', onFocusIn);
    return () => node?.removeEventListener('focusin', onFocusIn);
  }, [open]);

  // Dismiss when clicking away from the panel.
  useEffect(() => {
    if (!open) return undefined;
    function onDown(e) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        !returnFocusRef?.current?.contains?.(e.target)
      ) {
        onClose();
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onClose, returnFocusRef]);

  if (!open) return null;

  const statementHref = `${lang.urlPrefix || ''}/ukwa/info/accessibility`;

  const setContrast = (v) => {
    onUpdate({ contrast: v ? 'high' : 'normal' });
    announce(`${t.contrast} ${v ? t.on : t.off}`);
  };
  const setUnderline = (v) => {
    onUpdate({ underlineLinks: v });
    announce(`${t.underline} ${v ? t.on : t.off}`);
  };
  const setMotion = (v) => {
    onUpdate({ reduceMotion: v });
    announce(`${t.motion} ${v ? t.on : t.off}`);
  };

  return (
    <>
      {/* Dimming backdrop (mainly for small screens) — click to dismiss. */}
      <div className="fixed inset-0 z-[55] bg-black/20 sm:bg-transparent" aria-hidden="true" />

      <div
        id={id}
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-desc`}
        className="fixed z-[60] top-[min(5rem,10dvh)] right-2 sm:right-4 w-[min(20rem,calc(100vw-1rem))] max-h-[calc(100dvh-min(5rem,10dvh)-0.5rem)] overflow-y-auto overscroll-contain rounded-2xl border border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800 shadow-2xl ring-1 ring-black/5"
      >
        {/* Live region for screen readers */}
        <p className="sr-only" role="status" aria-live="polite">{announcement}</p>

        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-2">
          <div className="min-w-0">
            <h2 id={`${id}-title`} className="text-base font-semibold text-gray-900 dark:text-dark-100">
              {t.title}
            </h2>
            <p id={`${id}-desc`} className="mt-1 text-xs text-gray-500 dark:text-dark-400 leading-relaxed">
              {t.description}
            </p>
          </div>
          <button
            type="button"
            data-autofocus
            onClick={onClose}
            aria-label={t.close}
            className="shrink-0 p-1.5 rounded-md text-gray-600 dark:text-dark-300 hover:text-gray-900 dark:hover:text-dark-100 hover:bg-gray-100 dark:hover:bg-dark-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary"
          >
            <svg aria-hidden="true" focusable="false" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Controls */}
        <div className="px-4 pb-3 space-y-4">
          <Segmented
            name="a11y-theme"
            label={t.theme}
            value={theme === 'dark' ? 'dark' : 'light'}
            onChange={(v) => { onSetTheme(v); announce(`${t.theme}: ${v === 'dark' ? t.dark : t.light}`); }}
            options={[
              { value: 'light', label: t.light },
              { value: 'dark', label: t.dark },
            ]}
          />

          <Segmented
            name="a11y-size"
            label={t.textSize}
            value={prefs.fontScale}
            onChange={(v) => {
              onUpdate({ fontScale: v });
              const sizeLabel = { normal: t.sizeNormal, large: t.sizeLarge, larger: t.sizeLarger }[v] || v;
              announce(`${t.textSize}: ${sizeLabel}`);
            }}
            options={[
              { value: 'normal', label: t.sizeNormal, style: { fontSize: '0.8125rem' } },
              { value: 'large', label: t.sizeLarge, style: { fontSize: '0.9375rem' } },
              { value: 'larger', label: t.sizeLarger, style: { fontSize: '1.0625rem' } },
            ]}
          />

          <div className="pt-1 border-t border-gray-100 dark:border-dark-700 space-y-1">
            <Switch
              label={t.contrast}
              description={t.contrastDesc}
              checked={prefs.contrast === 'high'}
              onChange={setContrast}
            />
            <Switch
              label={t.underline}
              description={t.underlineDesc}
              checked={prefs.underlineLinks}
              onChange={setUnderline}
            />
            <Switch
              label={t.motion}
              description={t.motionDesc}
              checked={prefs.reduceMotion}
              onChange={setMotion}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 dark:border-dark-700 bg-gray-50/70 dark:bg-dark-900/40">
          <button
            type="button"
            onClick={() => { onReset(); announce(t.reseted); }}
            className="text-sm font-medium text-gray-600 dark:text-dark-300 hover:text-gray-900 dark:hover:text-dark-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary rounded-md px-1"
          >
            {t.reset}
          </button>
          <Link
            to={statementHref}
            onClick={onClose}
            className="text-sm font-medium text-accent-text hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary rounded-md px-1"
          >
            {t.statement}
          </Link>
        </div>
      </div>
    </>
  );
}
